import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SaveButton from "../../../../../components/SaveButton";
import { toast } from "../../../../../components/toastNotification";
import { accountAtom } from "../../../../Login/atoms/AccountAtom";
import {
  hasBank,
  isAddActionState,
  isEditActionState,
  isSavingBank,
  rowForEditBank,
} from "../atoms/atoms";
import useAddOfficeBank from "../hooks/useAddOfficeBank";
import useEditOfficeBank from "../hooks/useEditOfficeBank";
import { IOfficeBankDetails } from "../types";
import OfficeBankDetails from "./OfficeBankDetails";

interface OfficeBankFormProps {
  isAdd: boolean;
  setIsAdd;
  refetchBank;
}

export const OfficeBankForm: React.FC<OfficeBankFormProps> = ({
  isAdd,
  setIsAdd,
  refetchBank,
}) => {
  //Action state
  const [, setAddState] = useRecoilState(isAddActionState);
  const [actionEditState, setActionEditState] =
    useRecoilState(isEditActionState);
  //Data fetching
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditBank);
  const [newData, setNewData] = useState<IOfficeBankDetails>();

  const queryClient = useQueryClient();
  const userAccount = useRecoilValue(accountAtom);

  const [saveModal, setSaveModal] = useState(false);
  const [, setIsSaving] = useRecoilState(isSavingBank);
  const [hasBankName, setHasBank] = useRecoilState(hasBank);

  const useFormMethods = useForm<IOfficeBankDetails>({
    defaultValues: { ...rowForEdit },
  });

  const {
    formState: { isDirty },
  } = useFormMethods;

  // Add Bank
  const {
    mutate: OfficeBank,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddOfficeBank();
  const {
    mutate: editOfficeBank,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditOfficeBank();
  //Save
  const onSubmit = (data: IOfficeBankDetails) => {
    if (!isDirty) {
      setSaveModal(false);
      toast.fire("Saving Failed", "No changes have been made", "warning");
    } else if (isAdd) {
      setNewData({
        ...data,
        bankName: data?.bankName?.trim(),
        createdBy: userAccount?.userName,
        createdDate: new Date().toLocaleString(),
        isActive: true,
      });
      setSaveModal(true);
    } else if (actionEditState) {
      setNewData({
        ...data,
        bankName: data?.bankName?.trim(),
        isActive: data.isActive,
      });
      setSaveModal(true);
    }
  };

  const handleSave = () => {
    setSaveModal(false);
    if (isAdd) {
      OfficeBank(newData);
    } else {
      editOfficeBank(newData);
    }
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setHasBank(false);
    setIsSaving(false);
    setIsAdd(false);
    setActionEditState(false);
  };

  // Add Result
  if (addSuccess && addResult) {
    if (addResult.status === 501 || addResult.data === "Duplicate") {
      toast.fire("Failed", "Office Bank already exists.", "error");
      setIsSaving(false);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Office Bank added successfully.", "success");
      queryClient.invalidateQueries("GetOfficeBank");
      setAddState(false);
      setIsAdd(false);
      setIsSaving(false);
      setHasBank(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "Office Bank added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to add Office Bank! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetOfficeBank");
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to add Office Bank! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetOfficeBank");
      setAddState(false);
    }

    refetchBank();
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Office Bank! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetOfficeBank");
    setAddState(false);
  }

  // Edit Result
  if (editSuccess && editResult) {
    if (editResult.data === "Duplicate") {
      toast.fire("Failed", "Office Bank already exists.", "error");
      queryClient.resetQueries("GetOfficeBank");
      setIsSaving(false);
    } else if (
      (editResult && editResult.status === 201) ||
      editResult.status === 200
    ) {
      toast.fire("Success", "Office Bank Updated Successfully.", "success");
      queryClient.invalidateQueries("GetOfficeBank");

      setRowForEdit(undefined);
      setActionEditState(false);
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Office Bank updated successfully, but " +
            editResult.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowForEdit(undefined);
      } else {
        setIsSaving(false);
        setRowForEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to Update Office Bank! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetOfficeBank");
      setActionEditState(false);
    }
    refetchBank();
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    toast.fire(
      "Server Error",
      "Failed to Update Office Bank! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetOfficeBank");
    setActionEditState(false);
  }

  return (
    <>
      <div className="pl-3 space-y-2 border-l-2 pb-4 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg">
              <div className="font-bold pt-1 pb text-primary text-base">
                {actionEditState ? "Edit Office Bank" : " Add New Bank"}
              </div>
              <div className="flex justify-between gap-2 mr-2 pb-2 pt-3">
                <SaveButton
                  className={`flex items-left shadow-md lg:text-center font-semibold text-white justify-left gap-x-1 rounded-full bg-senary hover:bg-senary px-3 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary focus:ring-opacity-70
                  ${isAdd && !hasBankName ? "opacity-50" : "opactiy-100"}`}
                  disabled={isAdd && !hasBankName}
                  onClick={() => setSaveModal(true)}
                  type="submit"
                />
                <CancelButton
                  onClick={(event) => {
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 mr-3 border-secondary"></div>
          </form>
          <div>
            <OfficeBankDetails isAdd={isAdd} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={`${isAdd ? "Add " : "Update"} Office Bank`}
          qConfirmation={`Are you sure you want to ${
            isAdd ? "add" : "update"
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default OfficeBankForm;
